import { useCallback, useEffect, useRef, useState } from 'react'

// Canonical single-audio preview state machine, shared by the voice gallery and
// the playground's VoicePicker. Only one clip plays at a time; starting a new one
// (or toggling the active one off) fades the previous out over 200ms rather than
// hard-pausing, so switching voices never clicks. Exposes the currently playing
// id so callers can render a single "playing" highlight.
const FADE_MS = 200

export interface UseAudioPreviewResult {
  /** The id of the clip currently playing, or null. */
  playingId: string | null
  /** Play `url` under `id`; toggles off if `id` is already playing. */
  toggle: (id: string, url?: string) => void
  /** Stop any playback (with fade-out) and clear the highlight. */
  stop: () => void
}

/**
 * @param resetKey When it changes (e.g. the language's voice set changes), any
 *   in-flight preview is stopped AND the highlight cleared — so a stale
 *   `playingId` can't linger on a voice that's no longer visible.
 */
export function useAudioPreview(resetKey?: unknown): UseAudioPreviewResult {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Fade volume 1→0 over 200ms, then pause. The interval captures its own audio
  // reference, so it completes even after `audioRef` has moved on.
  const fadeOutAndPause = useCallback((audio: HTMLAudioElement) => {
    const step = 50 // ms per tick
    const ticks = FADE_MS / step
    const volumeStep = audio.volume / ticks
    const interval = setInterval(() => {
      if (audio.volume > volumeStep) {
        audio.volume = Math.max(0, audio.volume - volumeStep)
      } else {
        audio.volume = 0
        audio.pause()
        clearInterval(interval)
      }
    }, step)
  }, [])

  const stop = useCallback(() => {
    if (audioRef.current) {
      fadeOutAndPause(audioRef.current)
      audioRef.current = null
    }
    setPlayingId(null)
  }, [fadeOutAndPause])

  const toggle = useCallback(
    (id: string, url?: string) => {
      // Toggle off if this id is already previewing.
      if (playingId === id) {
        if (audioRef.current) {
          fadeOutAndPause(audioRef.current)
          audioRef.current = null
        }
        setPlayingId(null)
        return
      }
      // Fade out whatever is currently playing before starting the new clip.
      if (audioRef.current) {
        fadeOutAndPause(audioRef.current)
        audioRef.current = null
      }
      if (!url) {
        setPlayingId(null)
        return
      }
      const audio = new Audio(url)
      audioRef.current = audio
      setPlayingId(id)
      audio.play().catch(() => {
        setPlayingId(null)
        audioRef.current = null
      })
      audio.onended = () => {
        setPlayingId(null)
        audioRef.current = null
      }
    },
    [playingId, fadeOutAndPause]
  )

  // Stop playback and clear the highlight when the voice set changes or on
  // unmount. Clearing `playingId` here (not just pausing) is what prevents a
  // phantom "playing" highlight after the underlying voice disappears.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingId(null)
    }
  }, [resetKey])

  return { playingId, toggle, stop }
}

export default useAudioPreview
